// Catalog.ID Client — Pending Relay-Consent Store
// ============================================================================
// Bridges the `oidc-consent` hub mode (desktop-handlers.ts) and the OIDC
// consent pane. The handler registers a resolver under the OIDC `request_id`
// before opening the pane; when the user approves or denies, the pane calls
// `resolveRelayConsent` and — if a relay was waiting — the result travels back
// over the hub instead of a browser redirect to the RP.
//
// A store miss (resolveRelayConsent → false) means the pane was opened by the
// ordinary redirect flow (standalone /oidc/consent route) and must fall back
// to redirect delivery. If the handler times out and cancels the entry before
// the user acts, a later Approve also falls back to redirect delivery — the
// desktop caller has already reported failure by then.
// ============================================================================

export interface ConsentRelayResult {
    outcome: 'approved' | 'denied';
    /** Present when outcome === 'approved'. */
    code?: string;
    state?: string;
    /** The issuer that minted `code` (RFC 9207, docs/cid-governance.md M7b):
     *  the module exchanges the code there, and only after checking it is the
     *  node it authorized at. */
    iss?: string;
}

const pending = new Map<string, (result: ConsentRelayResult) => void>();

/** Register a resolver for `oidcRequestId` and return the promise the hub
 *  handler awaits. One pending entry per request id. */
export function registerRelayConsent(oidcRequestId: string): Promise<ConsentRelayResult> {
    return new Promise<ConsentRelayResult>(resolve => {
        pending.set(oidcRequestId, resolve);
    });
}

/** Called by the consent pane after approve/deny. Returns true when a relay
 *  handler was waiting (the pane must then skip redirect delivery). */
export function resolveRelayConsent(oidcRequestId: string, result: ConsentRelayResult): boolean {
    const resolve = pending.get(oidcRequestId);
    if (!resolve) return false;
    pending.delete(oidcRequestId);
    resolve(result);
    return true;
}

export function hasRelayConsent(oidcRequestId: string): boolean {
    return pending.has(oidcRequestId);
}

/** Drop a pending entry without resolving it (handler timeout/cleanup). The
 *  awaiting promise is abandoned — the handler races it against its own
 *  timeout, so nothing leaks. */
export function cancelRelayConsent(oidcRequestId: string): void {
    pending.delete(oidcRequestId);
}
